import type { ComputedRef, InjectionKey } from 'vue'

import {
  PivotTableCaptions,
  PivotTableTooltips,
  PivotTableValueDescriptions,
  PivotTableValueRenderers,
} from '@types'

export const MIN_HEADER_WIDTH = 110
export const MIN_RESIZED_WIDTH = 32

export const DEFAULT_CONFIG_NAME = 'Untitled Pivot'

export const GRAND_TOTAL_CAPTION = 'Grand total'

export const DEEP_MARGIN = 20
export const PATH_DELIMITER = '@@'

export const PIVOT_TABLE_LAZY_COUNT = 10000

// "Expand all" runs one query per node; these limits prevent overloading with too many sequential queries or rows.
export const MAX_EXPAND_ALL_ROOT_ROWS = 200
export const MAX_EXPAND_ALL_TOTAL_ROWS = 5000

// Marks a node's trailing total row (Classic layout), appended after its children.
export const TOTAL_MARKER_KEY = '__pivotTotalMarker'

// Indicates a collapsed column-group; value is its depth in colsList.
export const COL_GROUP_DEPTH_KEY = '__pivotColGroupDepth'

// `openedRows` sentinel meaning "every row is open" — set by "Expand all"
// (`usePivotData`'s `revealAllLoaded` / `usePivotMemoryData`'s `handleExpand`).
export const ALL_OPENED_SENTINEL = 'all'
// Marks one path as explicitly closed back while `ALL_OPENED_SENTINEL` is active — `openedRows`
// otherwise has no way to say "open, except these": dropping the sentinel itself would close
// everything, not just this row. See `getIsOpened`/`addOpenedRow`/`removeOpenedRow`.
export const CLOSED_UNDER_ALL_PREFIX = '__pivotClosedUnderAll__'

export const CLICKABLE_TEXT_CLASS = 'underline hover:no-underline underline-offset-2 decoration-muted-foreground cursor-pointer'

// Provide/inject keys for chrome config the widget sets once and the grid reads deep in the
// tree. Symbols, typed via InjectionKey<T>, so inject() infers its return type with no generic.
export const CAPTIONS_PROVIDE_KEY: InjectionKey<ComputedRef<PivotTableCaptions>> = Symbol('pivotCaptions')
export const RENDERERS_PROVIDE_KEY: InjectionKey<ComputedRef<PivotTableValueRenderers>> = Symbol('pivotRenderers')
export const VALUE_DESCRIPTIONS_PROVIDE_KEY: InjectionKey<ComputedRef<PivotTableValueDescriptions | undefined>> = Symbol('pivotValueDescriptions')
export const LINKABLE_NODES_PROVIDE_KEY: InjectionKey<string[]> = Symbol('pivotLinkableNodes')
export const TOOLTIPS_PROVIDE_KEY: InjectionKey<ComputedRef<PivotTableTooltips>> = Symbol('pivotTooltips')
export const DISABLE_COLS_PROVIDE_KEY: InjectionKey<boolean> = Symbol('pivotDisableCols')
export const CLICKABLE_NODES_PROVIDE_KEY: InjectionKey<string[]> = Symbol('pivotClickableNodes')
export const CLICKABLE_VALUES_PROVIDE_KEY: InjectionKey<boolean> = Symbol('pivotClickableValues')
/** Value fields whose numeric cells render red when negative, green when positive (zero stays neutral). */
export const SIGN_COLORED_FIELDS_PROVIDE_KEY: InjectionKey<string[]> = Symbol('pivotSignColoredFields')
